import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { feedbacksDb } from '../db/index.js';
import type { Feedback } from '../../types/feedback.js';
import {
  countUnread,
  isFeedbackUnread,
  readStorageKey,
} from '../utils/feedbackUnread.js';

// ---------------------------------------------------------------------------
// 既読状態の外部ストア（localStorage を backing にしたモジュール singleton）。
//
// useFeedbackUnread は FAB（バッジ表示）と MyFeedbackList（既読化する側）の
// 両方から別々に呼ばれる。フックローカル state で再計算をトリガすると一方の
// markRead が他方に伝播せず「読んだのにバッジが消えない」不具合になるため、
// useSyncExternalStore で全インスタンスを同時に更新する。
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();
let storeVersion = 0;

function emit(): void {
  storeVersion += 1;
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): number {
  return storeVersion;
}

/** localStorage から既読 epoch(ms) を取得（不可なら null） */
function getSeen(id: string): number | null {
  try {
    const v = localStorage.getItem(readStorageKey(id));
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

/** localStorage に既読 epoch を保存し、全 subscriber に通知（失敗しても実害なし） */
function setSeen(id: string, epoch: number): void {
  try {
    localStorage.setItem(readStorageKey(id), String(epoch));
  } catch {
    /* 保存できない場合は未読表示が残るだけ。握りつぶす。 */
  }
  emit();
}

export interface FeedbackUnread {
  feedbacks: Feedback[];
  isPending: boolean;
  unreadCount: number;
  isUnread: (fb: Feedback) => boolean;
  markRead: (fb: Feedback) => void;
}

/**
 * 投稿者自身のフィードバック一覧を取得し、管理者返信の未読状態を算出するフック。
 *
 * 既読位置は localStorage に保持し（[[isFeedbackUnread]] 参照）、外部ストア経由で
 * markRead を全インスタンスに伝播する。FAB が全画面に常設されるため、ウィンドウ復帰時に
 * 他端末で来た返信へ気づけるよう、このクエリだけ refetchOnWindowFocus を有効にする
 * （QueryClient の既定は false: src/App.tsx）。
 *
 * 取得は findByOwner の既定（直近100件）まで。それ以前の古いスレッドの未読は対象外。
 */
export function useFeedbackUnread(
  uid: string | null | undefined,
): FeedbackUnread {
  // 既読ストアの更新を購読。markRead 後に isUnread / unreadCount を再評価させる。
  const version = useSyncExternalStore(subscribe, getSnapshot);

  const q = useQuery({
    queryKey: ['feedbacks', 'mine', uid],
    queryFn: () => feedbacksDb.findByOwner(uid as string),
    enabled: !!uid,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const feedbacks = useMemo(() => q.data ?? [], [q.data]);

  const markRead = useCallback((fb: Feedback) => {
    if (!fb.lastMessageAt) return;
    setSeen(fb.id, fb.lastMessageAt.getTime());
  }, []);

  const isUnread = useCallback(
    (fb: Feedback) => isFeedbackUnread(fb, getSeen(fb.id)),
    // version を依存に含め、既読更新後に最新の localStorage で再判定する
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version],
  );

  const unreadCount = useMemo(
    () => countUnread(feedbacks, getSeen),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feedbacks, version],
  );

  return {
    feedbacks,
    isPending: !!uid && q.isPending,
    unreadCount,
    isUnread,
    markRead,
  };
}
